import { useState, useEffect, useCallback } from 'react'
import { searchBGGGames, getBGGGameDetails, BGGGameDetails } from '@/services/bgg-api'

interface BGGSearchHookState {
  results: BGGGameDetails[]
  isLoading: boolean
  error: string | null
}

interface BGGSearchHookResult extends BGGSearchHookState {
  search: (query: string) => void
  reset: () => void
  getGameById: (id: string) => Promise<BGGGameDetails | null>
}

/**
 * A hook for searching BoardGameGeek and fetching game details
 * @param initialQuery - Optional initial search query
 * @param autoSearch - Whether to perform search automatically with initialQuery
 * @returns Object containing search results, loading state, error, and search functions
 */
export function useBGGGameSearch(
  initialQuery: string = '',
  autoSearch: boolean = false
): BGGSearchHookResult {
  const [query, setQuery] = useState<string>(initialQuery)
  const [state, setState] = useState<BGGSearchHookState>({
    results: [],
    isLoading: autoSearch && initialQuery.length > 0,
    error: null,
  })

  // Effect to perform search when query changes
  useEffect(() => {
    let isMounted = true

    if (!query || query.length < 2) {
      setState(s => ({ ...s, results: [], isLoading: false, error: null }))
      return
    }

    const fetchGames = async () => {
      setState(s => ({ ...s, isLoading: true, error: null }))

      try {
        // First search for games by name
        const searchResult = await searchBGGGames(query)

        if (!isMounted) return

        if (searchResult.error) {
          setState(s => ({ ...s, isLoading: false, error: searchResult.error }))
          return
        }

        if (!searchResult.data || searchResult.data.length === 0) {
          setState(s => ({ ...s, results: [], isLoading: false }))
          return
        }

        // For the top 5 results, get details
        const detailPromises = searchResult.data.slice(0, 5).map(game => getBGGGameDetails(game.id))

        const detailResults = await Promise.all(detailPromises)

        if (!isMounted) return

        const gameDetails = detailResults
          .filter(result => result && !result.error && result.data)
          .map(result => result.data)
          .filter(Boolean) as BGGGameDetails[]

        setState(s => ({ ...s, results: gameDetails, isLoading: false }))
      } catch (error) {
        if (!isMounted) return
        setState(s => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : 'An error occurred during BGG search',
        }))
      }
    }

    if (query) {
      fetchGames()
    }

    return () => {
      isMounted = false
    }
  }, [query])

  // Initial search if autoSearch is true
  useEffect(() => {
    if (autoSearch && initialQuery) {
      setQuery(initialQuery)
    }
  }, [autoSearch, initialQuery])

  // Memoize functions to prevent unnecessary re-renders
  const search = useCallback((newQuery: string) => {
    setQuery(newQuery)
  }, [])

  const reset = useCallback(() => {
    setQuery('')
    setState({
      results: [],
      isLoading: false,
      error: null,
    })
  }, [])

  const getGameById = useCallback(async (id: string): Promise<BGGGameDetails | null> => {
    try {
      const { data, error } = await getBGGGameDetails(id)
      if (error) throw new Error(error)
      return data
    } catch (e) {
      setState(s => ({
        ...s,
        error: e instanceof Error ? e.message : 'Failed to fetch game details',
      }))
      return null
    }
  }, [])

  return {
    ...state,
    search,
    reset,
    getGameById,
  }
}

export default useBGGGameSearch
